import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/**
 * Custom multi-select dropdown — a checkbox list, portal-positioned and
 * theme-matched to AdminSelect (deliberately NOT the native <select>).
 * Used for column filters that accept several values at once. Selecting
 * an option toggles it without closing the menu, so several can be picked
 * in one pass.
 *
 * Props:
 *   value        - array of selected values
 *   onChange     - (nextArray) => void
 *   options      - [{ value, label, hint? }] or ["string", ...]
 *   placeholder  - trigger text when nothing is selected
 *   disabled
 */
export default function MultiSelect({
  value = [],
  onChange,
  options = [],
  placeholder = "Any",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState(null);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const btnRef = useRef(null);
  const listRef = useRef(null);

  const normalized = options.map((o) =>
    typeof o === "string" ? { value: o, label: o } : o
  );
  const selectedSet = new Set(value.map(String));
  const chosen = normalized.filter((o) => selectedSet.has(String(o.value)));

  const triggerLabel =
    chosen.length === 0 ? placeholder
    : chosen.length === 1 ? chosen[0].label
    : `${chosen.length} selected`;

  const openDropdown = () => {
    if (disabled) return;
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const dropUp = spaceBelow < 280 && r.top > 280;
      setDropPos({
        top: dropUp ? undefined : r.bottom + 4,
        bottom: dropUp ? window.innerHeight - r.top + 4 : undefined,
        left: r.left,
        width: r.width,
      });
    }
    setHighlightIdx(-1);
    setOpen(true);
  };

  // Close on outside click / scroll. The dropdown itself stops mousedown
  // propagation, so clicks on options don't count as "outside".
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (!btnRef.current?.contains(e.target)) setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onScroll);
    };
  }, [open]);

  useEffect(() => {
    if (highlightIdx >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll(".admin-select-opt");
      items[highlightIdx]?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightIdx]);

  const toggle = (v) => {
    const key = String(v);
    const next = selectedSet.has(key)
      ? value.filter((x) => String(x) !== key)
      : [...value, v];
    onChange(next);
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openDropdown();
      }
      return;
    }
    if (e.key === "Escape") { setOpen(false); return; }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, normalized.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if ((e.key === "Enter" || e.key === " ") && highlightIdx >= 0) {
      e.preventDefault();
      toggle(normalized[highlightIdx].value);
    }
  };

  return (
    <div className={`admin-select${open ? " open" : ""}${disabled ? " disabled" : ""}`}>
      <button
        ref={btnRef}
        type="button"
        className={`admin-select-trigger${chosen.length === 0 ? " placeholder" : ""}`}
        onClick={() => (open ? setOpen(false) : openDropdown())}
        onKeyDown={onKeyDown}
        disabled={disabled}
        tabIndex={0}
      >
        <span className="admin-select-value">{triggerLabel}</span>
        {chosen.length > 0 && <span className="ms-count">{chosen.length}</span>}
        <svg className={`admin-select-chevron${open ? " open" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && dropPos && createPortal(
        <div
          className="admin-select-dropdown"
          ref={listRef}
          style={{
            position: "fixed",
            top: dropPos.top,
            bottom: dropPos.bottom,
            left: dropPos.left,
            width: dropPos.width,
            zIndex: 99999,
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={onKeyDown}
        >
          <div className="admin-select-options">
            {normalized.length === 0 && (
              <div className="admin-select-empty">No options</div>
            )}
            {normalized.map((o, idx) => {
              const isSel = selectedSet.has(String(o.value));
              return (
                <div
                  key={o.value}
                  className={`admin-select-opt${isSel ? " selected" : ""}${idx === highlightIdx ? " highlighted" : ""}`}
                  onMouseDown={(e) => { e.preventDefault(); toggle(o.value); }}
                  onMouseEnter={() => setHighlightIdx(idx)}
                >
                  <span className={`ms-checkbox${isSel ? " checked" : ""}`}>
                    {isSel && (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width="12" height="12"><polyline points="20 6 9 17 4 12" /></svg>
                    )}
                  </span>
                  <span style={{ flex: 1 }}>{o.label}</span>
                  {o.hint && <span className="admin-select-hint">{o.hint}</span>}
                </div>
              );
            })}
          </div>
          {chosen.length > 0 && (
            <div className="admin-select-footer">
              <button
                type="button"
                className="ms-clear"
                onMouseDown={(e) => { e.preventDefault(); onChange([]); }}
              >
                Clear selection
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
